import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { DocumentRichText } from '../documents/form/inputs/DocumentRichText';
import {
    createFolder,
    updateFolder,
    listFolderCategories,
    FolderSummary,
} from '../../services/api/folderService';
import { useToast } from '../../hooks/use-toast';
import { CategoryPicker } from '../ui-components/category/CategoryPicker';
import {
    Category,
    CategoryUsage,
} from '../ui-components/category/categoryColors';

type FolderForDialog = Pick<
    FolderSummary,
    '_id' | 'title' | 'description' | 'category'
>;

interface CreateEditFolderDialogProps {
    mode: 'create' | 'edit';
    folder?: FolderForDialog;
    parent?: { _id: string; title: string };
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSaved?: (folderId: string) => void;
}

interface FormValues {
    title: string;
    description: string;
}

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 2000;

export const CreateEditFolderDialog = ({
    mode,
    folder,
    parent,
    open,
    onOpenChange,
    onSaved,
}: CreateEditFolderDialogProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();

    const form = useForm<FormValues>({
        defaultValues: { title: '', description: '' },
    });
    const [category, setCategory] = useState<Category | null>(null);
    const [renamePending, setRenamePending] = useState<{
        from: Category;
        to: Category;
    } | null>(null);
    const [existingCategories, setExistingCategories] = useState<
        CategoryUsage[]
    >([]);

    useEffect(() => {
        if (!open) return;
        form.reset(
            mode === 'edit' && folder
                ? { title: folder.title, description: folder.description ?? '' }
                : { title: '', description: '' }
        );
        setCategory(
            mode === 'edit' && folder ? (folder.category ?? null) : null
        );
        setRenamePending(null);
        const load = async () => {
            try {
                const result = await listFolderCategories();
                setExistingCategories(result);
            } catch {
                // Silent — picker will fall back to an empty list
            }
        };
        load();
    }, [open, mode, folder, form]);

    const handleSubmit = async (values: FormValues) => {
        try {
            if (mode === 'create') {
                const { _id } = await createFolder({
                    title: values.title.trim(),
                    description: values.description,
                    category: parent ? undefined : category,
                    parentId: parent?._id,
                });
                onOpenChange(false);
                if (onSaved) {
                    onSaved(_id);
                } else {
                    navigate(`/group/${_id}`);
                }
            } else if (folder) {
                await updateFolder(folder._id, {
                    title: values.title.trim(),
                    description: values.description,
                    category: parent ? undefined : category,
                    renameCategory: parent
                        ? undefined
                        : (renamePending ?? undefined),
                });
                onOpenChange(false);
                toast({ title: t('folders.create.toasts.updated') });
                onSaved?.(folder._id);
            }
        } catch {
            toast({
                title: t('folders.common.error'),
                description:
                    mode === 'create'
                        ? t('folders.create.toasts.createFailed')
                        : t('folders.create.toasts.updateFailed'),
                variant: 'destructive',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'edit'
                            ? t('folders.create.editTitle')
                            : parent
                              ? t('folders.create.createSubTitle', {
                                    parent: parent.title,
                                })
                              : t('folders.create.createTitle')}
                    </DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(handleSubmit)}
                        className="space-y-4"
                    >
                        <FormField
                            control={form.control}
                            name="title"
                            rules={{
                                required: t('folders.create.nameRequired'),
                                maxLength: {
                                    value: TITLE_MAX,
                                    message: t('folders.create.maxChars', {
                                        count: TITLE_MAX,
                                    }),
                                },
                                validate: (v) =>
                                    v.trim().length > 0 ||
                                    t('folders.create.nameEmpty'),
                            }}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('folders.create.nameLabel')}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            maxLength={TITLE_MAX}
                                            autoFocus
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {!parent && (
                            <FormItem>
                                <FormLabel>
                                    {t('folders.category.label')}{' '}
                                    <span className="text-foreground">
                                        {t(
                                            'folders.create.descriptionOptional'
                                        )}
                                    </span>
                                </FormLabel>
                                <CategoryPicker
                                    value={category}
                                    onChange={setCategory}
                                    existing={existingCategories}
                                    mode={mode}
                                    onRename={setRenamePending}
                                />
                            </FormItem>
                        )}
                        <FormField
                            control={form.control}
                            name="description"
                            rules={{
                                maxLength: {
                                    value: DESCRIPTION_MAX,
                                    message: t('folders.create.maxChars', {
                                        count: DESCRIPTION_MAX,
                                    }),
                                },
                            }}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('folders.create.descriptionLabel')}{' '}
                                        <span className="text-foreground">
                                            {t(
                                                'folders.create.descriptionOptional'
                                            )}
                                        </span>
                                    </FormLabel>
                                    <FormControl>
                                        <DocumentRichText
                                            value={field.value}
                                            onChange={field.onChange}
                                            className="[&_.ProseMirror]:min-h-72"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={form.formState.isSubmitting}
                            >
                                {t('folders.create.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={form.formState.isSubmitting}
                            >
                                {mode === 'create'
                                    ? t('folders.create.create')
                                    : t('folders.create.save')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
